// 🚪🕯️ BETWEEN ROOMS (Nina 09-24): what plays while you walk from one room of
// the house to the next. Pure rules, copy and timing live here; the drawing
// and the timeline are components/ChatGame/RoomTransit.js. Every way into a
// room (picker, typed /room, invites, the calendar's Go, the ?room= link)
// rides ONE mover (GameStepPage goRoom → handleInterjectionSubmit), so the
// transit starts there, on the tap, before the house answers.
//
// Nina's split (09-24, after the v1 mockup of four styles):
//   A · footsteps       places you walk to
//   B · the little door  rooms behind a real door (the bathroom is /knock's door)
//   C · the keyhole      heading out: an outing, or an outdoor room while its event is on
//   D · the candle hop   NOT a room move: the house's "is thinking" dots (CandleHop.js)
// Which one plays is FIXED PER ROOM (not random), so a room always arrives
// the same way and the house has a layout you learn.
//
// ⭐ It never adds a wait: it ends the moment the house has answered AND the
//   painting has decoded, and on desktop the walk comes OUT of the arrival
//   card's 4.5 seconds (arrivalCardMs), so the chat returns when it does today.
// ⭐ Player copy (rule 9): short, no em dashes (pinned in roomTransit.test.js).
import { ARRIVE_MS, ROOM_ICONS } from './roomView';

export const DOOR_ROOMS = new Set(['library', 'bathroom', 'office', 'den', 'music', 'attic']);
export const OUTDOOR_ROOMS = new Set(['roof', 'porch']);
export const OUTING_ROOMS = new Set(['orchard', 'lighthouse', 'maze']); // 🌽 09-28 maze: the third outing (a candle keyhole: the maze is lanterns)
// A walk out of the house on a wet day leaves wet prints (the garden path, the porch steps).
const WET_ROOMS = new Set(['porch', 'roof', 'workshop', 'greenhouse']);
const WET_KINDS = new Set(['rain', 'storm']);

export const TRANSIT_MS = {
  ENTER: 350,        // the loader's own entrance: the floor, however fast the house is
  STEP: 290,         // one footprint
  LAST_STEPS: 300,   // A: the two steps that reach the door
  OPEN: 450,         // the door swings open
  EXPAND: 560,       // the doorway grows to fill the screen
  IRIS_CLOSE: 480,   // C: the room closes down to a keyhole
  IRIS_LIGHT: 300,   // C: the dark hall lifts inside the keyhole
  IRIS_OPEN: 640,    // C: the keyhole opens on the new room
  SLOW_AFTER: 900,   // B: the door only knocks once the house is actually slow
  REFUSE: 1100,      // a locked or unknown door rattles this long
  GONE: 360,         // the loader's fade out
  SETTLE: 150,       // the overlay outlives the wallpaper swap by this much (no flash)
  CARD_FLOOR: 2200,  // the arrival card never gets shorter than this
  ART_WAIT_MAX: 2500, // a painting that will not decode never holds the walk longer
  SAFETY: 12000,     // a stuck request ends the walk anyway
  // 🐢 09-24 fix (sweep #151 seam audit, Nina: fix it): a slow house must not
  // hold the page. Past this, a walk that has not started its reveal STEPS
  // ASIDE: the page comes back, the old picture stays, the candle hop waits in
  // the rooms door (D, "for when we're waiting"), and the new painting fades
  // in when the answer lands. p90 of a house answer is 1.9s (measured 09-24).
  CURTAIN_MAX: 2000,
  FADE_IN: 600,      // the stepped-aside walk's landing: the painting fades in
};

// The house registry (backend housePlaces.PLACES): id → [place, label].
// Only used to recognise a LOCKED room by name (the open list comes from the
// payload); roomTransitParity.test.js pins it to the backend's. Outings are
// NOT in it on purpose: the server only knows an outing while it runs, and a
// closed one answers "No room by that name" (so the door rattles here too).
export const ROOM_NAMES = {
  dining: ['dining room', 'the dining room'],
  sunroom: ['sun room', 'the sun room'],
  library: ['library', 'the library'],
  greenhouse: ['greenhouse', 'the greenhouse'],
  bathroom: ['hall bathroom', 'the hall bathroom'],
  porch: ['porch', 'the porch'],
  attic: ['attic', 'the attic'],
  roof: ['roof', 'the roof'],
  workshop: ['workshop', 'the workshop shed'],
  garage: ['garage', 'the garage'],
  music: ['music room', 'the music room'],
  gym: ['gym', 'the gym'],
  office: ['office', 'the office'],
  den: ['den', 'the den'],
};
// roomCamera.HOME_ALIASES, mirrored (the parity test runs both resolvers).
const HOME_ALIASES = new Set(['house', 'back', 'main', 'main room', 'the house', 'home', 'downstairs']);
export const UNKNOWN_ROOM = '?';

const formsOf = (id, place, label) => [id, place, label, String(label || '').replace(/^the\s+/, '')]
  .filter(Boolean).map((s) => String(s).toLowerCase());

/** The typed /room text → the room it will land in: 'house', an open room's
 *  id, a known-but-locked room's id (the door will rattle), UNKNOWN_ROOM (no
 *  such room: the door rattles too), or null = no walk at all (an empty
 *  /room or "list" just lists the rooms). Same forms as roomCamera.resolveRoom. */
export function resolveRoomText(text, rooms) {
  const t = String(text == null ? '' : text).trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t || t === 'list' || t === 'rooms') return null;
  const bare = t.replace(/^the\s+/, '');
  if (HOME_ALIASES.has(t) || HOME_ALIASES.has(bare)) return 'house';
  const hit = (Array.isArray(rooms) ? rooms : []).find((r) => r && r.id && formsOf(r.id, r.place, r.label || '').some((f) => f === t || f === bare));
  if (hit) return hit.id;
  const known = Object.keys(ROOM_NAMES).find((id) => formsOf(id, ...ROOM_NAMES[id]).some((f) => f === t || f === bare));
  return known || UNKNOWN_ROOM;
}

/** THE RULE: which loader plays for a move to `to`.
 *  rooms = the payload's open rooms ({ id, outing? }) · outingId = the outing
 *  running now · eventRoom = the room a live located event is inviting to. */
export function pickTransit({ to, rooms, outingId, eventRoom, reduceMotion } = {}) {
  if (!to) return null;
  if (reduceMotion) return 'still';
  const entry = (Array.isArray(rooms) ? rooms : []).find((r) => r && r.id === to);
  if ((entry && entry.outing) || to === outingId || OUTING_ROOMS.has(to)) return 'keyhole';
  if (OUTDOOR_ROOMS.has(to) && eventRoom === to) return 'keyhole';
  if (to === UNKNOWN_ROOM || DOOR_ROOMS.has(to)) return 'door';
  return 'steps';
}

/** What you see inside the keyhole: the lighthouse beam, the night sky for an
 *  outdoor event, a candle for everything else (the orchard's lantern). */
export function keyholeView(to) {
  if (to === 'lighthouse') return 'beam';
  if (OUTDOOR_ROOMS.has(to)) return 'stars';
  return 'candle';
}

/** Wet prints: a walk outside on a rain or storm day. */
export function wetSteps(to, weatherKind) {
  return WET_KINDS.has(weatherKind) && WET_ROOMS.has(to);
}

const WALK_LINES = {
  house: 'Back down to the main room…',
  dining: 'Down the hall to the dining room…',
  sunroom: 'Following the sunshine…',
  library: 'Tiptoeing into the library…',
  greenhouse: 'Out back to the greenhouse…',
  bathroom: 'Getting in line for the bathroom…',
  porch: 'Out the front door…',
  attic: 'Up the creaky attic ladder…',
  roof: 'Out the window, up to the roof…',
  workshop: 'Down the garden path to the shed…',
  garage: 'Through the side door to the garage…',
  music: 'Following the out-of-tune piano…',
  gym: 'Jogging to the gym, sort of…',
  office: 'Quietly, to the office…',
  den: 'Sinking toward the good couch…',
  orchard: 'Off to the orchard…',
  lighthouse: 'Up the lighthouse stairs…',
  maze: 'Out to the corn maze…', // 🌽 09-28 maze
};
const WALK_LINES_DARK = { sunroom: 'Following the starlight…' };
export const TRYING_LINE = 'Trying the door…';
// While a locked (or unknown) door rattles: the walking line would be a lie
// ("Sinking toward the good couch…"); the toast after it carries the house's words.
export const REFUSE_LINE = "The door won't budge.";

/** The one short line shown while you walk. `dark` = the destination's night
 *  picture is the one showing (the same rule as the room's entry line). */
export function walkLine(roomId, dark) {
  if (dark && WALK_LINES_DARK[roomId]) return WALK_LINES_DARK[roomId];
  return WALK_LINES[roomId] || TRYING_LINE;
}
export const WALK_LINE_ROOMS = Object.keys(WALK_LINES);

/** How long the reveal runs once the house has answered, per loader. */
export function revealMs(kind) {
  if (kind === 'steps') return TRANSIT_MS.LAST_STEPS + TRANSIT_MS.OPEN - 60 + TRANSIT_MS.EXPAND;
  if (kind === 'door') return TRANSIT_MS.OPEN + TRANSIT_MS.EXPAND;
  if (kind === 'keyhole') return TRANSIT_MS.IRIS_LIGHT + TRANSIT_MS.IRIS_OPEN;
  return 0;
}

// 📱 09-25 (Nina: "go"): phones walk on the whole screen and get the arrival
// postcard too, SHORTER than desktop's (people hop rooms more on a phone; any
// tap still skips). The overlay sits over the phone dock (5000) and nav
// (5100), under the peek (5200); the postcard one notch above the walk.
export const PHONE_WALK_Z = 5150;
export const PHONE_CARD_MS = 2500;
export const PHONE_WHO_DELAY_MS = 700;

/** The arrival card's time after a walk: the walk comes OUT of the card's
 *  4.5s, so you are back in the chat when you are today (never under the floor).
 *  netMs = tap → the house answered · shownMs = tap → the room filled the screen. */
export function arrivalCardMs(netMs, shownMs) {
  const net = Number.isFinite(netMs) ? Math.max(0, netMs) : 0;
  const shown = Number.isFinite(shownMs) ? Math.max(0, shownMs) : 0;
  return Math.max(TRANSIT_MS.CARD_FLOOR, Math.round(net + ARRIVE_MS - shown));
}

/** 👁 The picture the peek may show: only the room you are IN. While a walk
 *  holds the room you are LEAVING on the wallpaper (artRoom ≠ viewerRoom) the
 *  camera is already in the new room, so there is none, and the peek's openers
 *  (the ?room=…&view=peek link, an event's Go) wait for the walk to land
 *  (09-24 fix: they used to open on the previous room's painting). */
export function peekPictureUrl(masthead, artRoom, viewerRoom) {
  return (masthead && masthead.kind === 'art' && masthead.url && artRoom === viewerRoom) ? masthead.url : null;
}

/** 🐢 The rooms door while a slow room change waits (the walk stepped aside):
 *  { from, to, label } for the candle hop, or null = the door as always. */
const doorIcon = (id) => (id === 'house' ? '🕯️' : (ROOM_ICONS[id] || '🚪'));
export function walkingDoor(transit, rooms) {
  if (!transit || transit.phase !== 'aside') return null;
  const to = transit.to;
  const known = (Array.isArray(rooms) ? rooms : []).find((r) => r && r.id === to);
  return {
    from: doorIcon(transit.from),
    to: doorIcon(to),
    label: to === 'house' ? 'the main room' : ((known && known.label) || 'another room'),
  };
}

// Painting preload, memoised per url: starts on the tap so it downloads while
// the house answers. Resolves true (decoded) or false (failed), never rejects.
const artLoads = new Map();
export function preloadArt(url) {
  if (typeof url !== 'string' || !/^https:\/\//.test(url) || typeof Image === 'undefined') return Promise.resolve(false);
  if (artLoads.has(url)) return artLoads.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    const ok = () => resolve(true);
    const fail = () => { artLoads.delete(url); resolve(false); };
    img.onerror = fail;
    img.src = url;
    if (typeof img.decode === 'function') img.decode().then(ok, () => { if (img.complete && img.naturalWidth > 0) ok(); else fail(); });
    else img.onload = ok;
  });
  artLoads.set(url, p);
  return p;
}
